import React from 'react';
import {createRoot} from 'react-dom/client';
import {flushSync} from 'react-dom';
import {DrinksAdvisorApp} from './DrinksAdvisorApp';
import {Controller} from './host/controller';
import {EVENT_DEFINITIONS} from './host/eventRegistry';
import type {AdvisorInitOptions, AdvisorOptions, BrowserApi} from './host/types';
import {ADVISOR_VERSION} from './version';
import {resolveAgentContent} from './content';
import './styles/twe-drinks-advisor.css';
export type * from './host/types';
export {EVENT_DEFINITIONS} from './host/eventRegistry';
/** Available on the classic-script IIFE global for host documentation generation. */
export const callbackRegistry = EVENT_DEFINITIONS;

/**
 * Hydrates optional host-shell slots from React-owned content. Hosts only
 * provide structural elements such as the launcher button and mount point.
 */
function hydrateHostContent() {
  const content = resolveAgentContent();
  const contentValue = (path: string): unknown => path.split('.').reduce<unknown>((value, key) => (
    value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined
  ), content);

  document.querySelectorAll<HTMLElement>('[data-agent-content]').forEach(element => {
    const value = contentValue(element.dataset.agentContent || '');
    if (typeof value === 'string') element.textContent = value;
  });
  document.querySelectorAll<HTMLElement>('[data-agent-content-attribute]').forEach(element => {
    const [attribute, ...path] = (element.dataset.agentContentAttribute || '').split(':');
    const value = contentValue(path.join(':'));
    if (attribute && typeof value === 'string') element.setAttribute(attribute, value);
  });
  document.querySelectorAll<HTMLElement>('[data-agent-name]').forEach(element => {
    element.textContent = content.agentName;
  });
  document.title = content.pageTitle;
}

class AdvisorBoundary extends React.Component<{controller: Controller; children: React.ReactNode}, {failed: boolean}> {
  state = {failed: false};
  static getDerivedStateFromError() {
    return {failed: true};
  }
  componentDidCatch(error: Error) {
    this.props.controller.bus.error(error, {}, 'ADVISOR_RENDER_FAILED', 'rendering', true);
  }
  render() {
    const content = resolveAgentContent();
    return this.state.failed ? <div className="twe-drinks-advisor"><p role="alert">{content.response.friendlyError}</p></div> : this.props.children;
  }
}

let active: Controller | null = null;
export const version = ADVISOR_VERSION;

/** Creates an advisor instance using the established explicit mount-point API. */
export function create(options: AdvisorOptions) {
  if (active && !active.destroyed) throw new Error('Only one active Drinks Advisor instance is supported. Destroy the previous instance first.');
  const element = document.getElementById(options.elementId);
  if (!element) throw new Error(`Drinks Advisor mount element #${options.elementId} was not found.`);
  if (!(options.apiKey || options.config?.constructor?.apiKey || import.meta.env.VITE_CONSTRUCTOR_API_KEY)) {
    throw new Error('Constructor API key is missing. Configure apiKey, VITE_CONSTRUCTOR_API_KEY, or config.constructor.apiKey.');
  }

  const controller = new Controller(options);
  hydrateHostContent();
  const mount = document.createElement('div');
  mount.dataset.tweAdvisorRoot = 'true';
  element.appendChild(mount);
  const root = createRoot(mount);
  controller.dispose = () => {
    flushSync(() => root.unmount());
    mount.remove();
    if (active === controller) active = null;
  };
  active = controller;
  flushSync(() => root.render(<AdvisorBoundary controller={controller}><DrinksAdvisorApp controller={controller}/></AdvisorBoundary>));
  return {
    open: controller.open,
    close: controller.close,
    expand: controller.expand,
    restore: controller.restore,
    setDialogWidth: controller.setDialogWidth,
    resetConversation: controller.resetConversation,
    destroy: controller.destroy,
  };
}

/**
 * A concise host-facing initializer. It accepts asaRequestConfig before React
 * mounts and keeps create() available unchanged for current integrations.
 */
export function init(options: AdvisorInitOptions) {
  return create({...options, elementId: options.elementId || 'twe-shopping-agent'});
}

/** Supplies React-owned customer-facing copy to a host shell. */
export function getContent() {
  return resolveAgentContent();
}

const api: BrowserApi = {create, init, getContent, version, callbackRegistry};
window.TweShoppingAgent = api;
